import React from 'react';
import styled from 'styled-components';
import media from '../style/media';
import Spinner from './Spinner';
import { SessionState } from '@zakhealth/web-sdk';

// --- STYLED COMPONENTS (from your new design) ---

const Card = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  padding: 20px 16px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 10; /* Ensure it's above other elements */

  ${media.tablet`
    padding: 28px 24px;
  `}
`;

const Instruction = styled.p`
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin-bottom: 16px;
  line-height: 1.5;
  max-width: 400px; /* Constrain width for better readability */

  strong {
    font-weight: 700;
    color: #000;
  }
`;

const Button = styled.button`
  width: 100%;
  max-width: 340px;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.1s ease-in-out;
  color: #fff;
  background: #b71c1c;
  
  /* Touch optimization for tablets */
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  user-select: none;

  &:hover:not(:disabled) {
    background: #a11919;
  }

  &:active:not(:disabled) {
    background: #8c1616;
    transform: scale(0.98);
  }

  &:focus {
    outline: 2px solid #e24a4a;
    outline-offset: 2px;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

// --- COMPONENT INTERFACE ---

export interface IStartButton {
    isLoading: boolean;
    onClick: () => void;
    isMeasuring: boolean;
    errorMessage: string;
    infoMessage: string;
    sessionState: SessionState;
}

// --- COMPONENT LOGIC (Merged) ---

const StartButton = React.memo(({ isLoading, onClick, isMeasuring, errorMessage, infoMessage, sessionState }: IStartButton) => {
    // Case 1: If measuring and no error/info message → show nothing
    if (isMeasuring && !errorMessage && !infoMessage) {
        return null;
    }

    if (!sessionState) {
        return null;
    }
    return (
        <Card>
            {/* Case 2: Show instructions */}
            {!isMeasuring && errorMessage ? (
                <Instruction className="text-red-600">
                    We couldn't get the necessary vital signs. Please try again
                </Instruction>
            ) : !isMeasuring ? (
                <Instruction>
                    Ready to measure your <strong>Vital Signs?</strong> <br />
                    Sit still, ensure your face is well illuminated and avoid background light.
                </Instruction>
            ) : infoMessage ? (
                <Instruction>{infoMessage}</Instruction>
            ) : null}



            {/* Case 3: Show spinner or button */}
            {!isMeasuring && (
                isLoading ? (
                    <Spinner />
                ) : (
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClick();
                        }}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            onClick();
                        }}
                        type="button"
                    >
                        MEASURE NOW
                    </Button>
                )
            )}
        </Card>
    );
});

StartButton.displayName = 'StartButton';

export default StartButton;